import React from 'react';
import { MainInfoSection } from './MainInfoSection';
import { ApplicableToSection } from './ApplicableToSection';
import { PublishingSettingsSection } from './PublishingSettingsSection';
import {
  TAnnouncementPublishFor,
  TAnnouncementAppearIn,
  TAnnouncementRestrictType,
} from 'services/BackendApi/types/AnnouncementResponse';
import { IMultiSearchDropdownOption } from 'ui/MultiSearchDropdown/MultiSearchDropdownOptions';

interface IValidationErrors {
  hotelUuid?: boolean;
  title?: boolean;
  description?: boolean;
  appearIn?: boolean;
  activeFrom?: boolean;
  activeTo?: boolean;
  targeting?: boolean;
}

interface IAnnouncementEditFormProps {
  announcementEditState: {
    hotelUuid: string;
    title: string;
    description: string;
    restrictType: TAnnouncementRestrictType;
    countryCodes: string[];
    travelCompanyUuids: string[];
    isDraft: boolean;
    publishFor: TAnnouncementPublishFor;
    activeFrom: string;
    activeTo: string;
    appearIn: TAnnouncementAppearIn[];
  };
  validationErrors: IValidationErrors;
  isCreateMode?: boolean;
  initialTravelCompanyOptions?: IMultiSearchDropdownOption[];
  // Main Info section update functions
  updateHotelUuid: (hotelUuid: string) => void;
  updateTitle: (title: string) => void;
  updateDescription: (description: string) => void;
  // Applicable To section update functions
  updateRestrictType: (type: TAnnouncementRestrictType) => void;
  updateCountryCodes: (codes: string[]) => void;
  resetCountryCodes: () => void;
  updateTravelCompanyUuids: (uuids: string[]) => void;
  // Publishing Settings section update functions
  updateIsDraft: (isDraft: boolean) => void;
  updatePublishFor: (publishFor: TAnnouncementPublishFor) => void;
  updateActiveFrom: (date: string) => void;
  updateActiveTo: (date: string) => void;
  updateAppearIn: (locations: TAnnouncementAppearIn[]) => void;
}

export const AnnouncementEditForm = ({
  announcementEditState,
  validationErrors,
  isCreateMode = false,
  initialTravelCompanyOptions = [],
  updateHotelUuid,
  updateTitle,
  updateDescription,
  updateRestrictType,
  updateCountryCodes,
  resetCountryCodes,
  updateTravelCompanyUuids,
  updateIsDraft,
  updatePublishFor,
  updateActiveFrom,
  updateActiveTo,
  updateAppearIn,
}: IAnnouncementEditFormProps) => {
  return (
    <div className="announcement-edit-form space-y-10">
      {/* Main Info Section - Hotel, Title, Description */}
      <MainInfoSection
        hotelUuid={announcementEditState.hotelUuid}
        title={announcementEditState.title}
        description={announcementEditState.description}
        validationErrors={{
          hotelUuid: validationErrors.hotelUuid,
          title: validationErrors.title,
          description: validationErrors.description,
        }}
        updateHotelUuid={updateHotelUuid}
        updateTitle={updateTitle}
        updateDescription={updateDescription}
      />

      {/* Applicable To Section - Targeting modes (Country or TC Name) */}
      <ApplicableToSection
        restrictType={announcementEditState.restrictType || 'BY_COUNTRIES'}
        countryCodes={announcementEditState.countryCodes || []}
        travelCompanyUuids={announcementEditState.travelCompanyUuids || []}
        initialTravelCompanyOptions={initialTravelCompanyOptions}
        validationErrors={{
          targeting: validationErrors.targeting,
        }}
        updateRestrictType={updateRestrictType}
        updateCountryCodes={updateCountryCodes}
        resetCountryCodes={resetCountryCodes}
        updateTravelCompanyUuids={updateTravelCompanyUuids}
      />

      {/* Publishing Settings Section - Draft, Publish For, Active dates, Show In */}
      <PublishingSettingsSection
        isDraft={announcementEditState.isDraft !== undefined ? announcementEditState.isDraft : true}
        publishFor={announcementEditState.publishFor}
        activeFrom={announcementEditState.activeFrom}
        activeTo={announcementEditState.activeTo}
        appearIn={announcementEditState.appearIn}
        validationErrors={{
          activeFrom: validationErrors.activeFrom,
          activeTo: validationErrors.activeTo,
          appearIn: validationErrors.appearIn,
        }}
        updateIsDraft={updateIsDraft}
        updatePublishFor={updatePublishFor}
        updateActiveFrom={updateActiveFrom}
        updateActiveTo={updateActiveTo}
        updateAppearIn={updateAppearIn}
      />
    </div>
  );
};
